import React from 'react'
import { Helmet } from 'react-helmet-async'
import { siteConfig } from '../config/seo'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  noindex?: boolean
  nofollow?: boolean
}

/**
 * SEO Component - Manages meta tags for search engines and social media
 * 
 * This component dynamically updates the document head with SEO meta tags including:
 * - Title tags (unique per page)
 * - Meta descriptions
 * - Open Graph tags (Facebook, LinkedIn)
 * - Twitter Card tags
 * - Canonical URLs
 * - Robots directives
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   url="https://directconnectwales.co.uk/page"
 * />
 */
/**
 * The build ships static SEO tags in each prerendered page (marked with
 * data-sseo). Once React hydrates, Helmet manages its own copies — remove the
 * static ones so the rendered DOM never contains duplicate canonical/meta tags.
 */
const removeStaticSeoTags = () => {
  document.querySelectorAll('[data-sseo]').forEach((el) => el.remove())
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = siteConfig.defaultImage,
  url,
  type = 'website',
  noindex = false,
  nofollow = false,
}) => {
  React.useEffect(() => {
    removeStaticSeoTags()
  }, [])

  const siteName = siteConfig.name
  const baseUrl = siteConfig.url
  const defaultTitle = 'Full Fibre Broadband in Wales | Direct Connect Wales'
  const defaultDescription = siteConfig.defaultDescription
  const defaultKeywords = siteConfig.defaultKeywords

  // Construct full title (page title + site name)
  const fullTitle = title ? `${title} | ${siteName}` : defaultTitle
  const metaDescription = description || defaultDescription
  const metaKeywords = keywords || defaultKeywords
  const canonicalUrl = url ? `${baseUrl}${url}` : baseUrl
  const ogImage = image.startsWith('http') ? image : `${baseUrl}${image}`

  // Robots meta
  const robots = []
  if (noindex) robots.push('noindex')
  if (nofollow) robots.push('nofollow')
  if (robots.length === 0) robots.push('index', 'follow')

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={robots.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Tags */}
      <meta name="author" content={siteName} />
      <meta name="theme-color" content="#1976d2" />
    </Helmet>
  )
}

export default SEO

